import { useCallback, useMemo, useState } from "react";
import { AjnaIcon } from "./ajna-icon";
import { MorphoIcon } from "./morpho-icon";
import { useAppKit } from "@reown/appkit/react";
import { useQueryClient } from "@tanstack/react-query";
import { parseUnits } from "viem";
import { useAccount, useSwitchChain, useWalletClient } from "wagmi";

import { ACTIVE_CHAIN } from "@/config/chain";
import { USDC } from "@/config/tokens";
import { SUPPLY_VAULT } from "@/config/vault";
import { formatPercent, formatToken } from "@/lib/format";
import {
  approveVaultUsdc,
  depositToVault,
  redeemFromVault,
  withdrawFromVault,
} from "@/lib/vault/execute";
import { buildPublicClient } from "@/lib/wallet/clients";
import { useTokenBalance } from "@/hooks/use-token-balance";
import { useVault } from "@/hooks/use-vault";
import { useVaultPosition } from "@/hooks/use-vault-position";

import { ExecuteButton } from "./execute-button";

type SupplyMode = "supply" | "withdraw";

type SubmitState =
  | { kind: "idle" }
  | { kind: "pending" }
  | { kind: "mining"; hash: `0x${string}` }
  | { kind: "success"; hash: `0x${string}` }
  | { kind: "error"; message: string };

const sanitise = (raw: string): string => {
  const cleaned = raw.replace(/[^0-9.]/g, "");
  const parts = cleaned.split(".");
  return parts.length <= 1 ? cleaned : `${parts[0]}.${parts.slice(1).join("")}`;
};

const MODES: ReadonlyArray<{ id: SupplyMode; label: string }> = [
  { id: "supply", label: "Supply" },
  { id: "withdraw", label: "Withdraw" },
];

const ModeToggle = ({
  value,
  onChange,
}: {
  value: SupplyMode;
  onChange: (m: SupplyMode) => void;
}) => (
  <div
    role="radiogroup"
    aria-label="Supply mode"
    className="inline-flex items-center gap-0.5 p-0.5 rounded-[var(--radius-sm)]"
    style={{ border: "1px solid var(--line-2)" }}
  >
    {MODES.map((opt) => {
      const active = value === opt.id;
      return (
        <button
          key={opt.id}
          role="radio"
          aria-checked={active}
          type="button"
          onClick={() => onChange(opt.id)}
          className="px-3 py-1 rounded-[2px] font-mono text-[10px] tracking-[0.16em] uppercase transition-colors"
          style={{
            background: active ? "var(--surface-2)" : "transparent",
            color: active ? "var(--ink-1)" : "var(--ink-3)",
            fontWeight: 500,
          }}
        >
          {opt.label}
        </button>
      );
    })}
  </div>
);

const StatRow = ({
  label,
  value,
  hint,
  tone,
  last,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: string;
  last?: boolean;
}) => (
  <li
    className="flex items-baseline justify-between gap-6 pb-3"
    style={{ borderBottom: last ? "none" : "1px dashed var(--line-1)" }}
  >
    <span className="font-sans text-[12px] tracking-[0.04em]" style={{ color: "var(--ink-3)" }}>
      {label}
    </span>
    <span className="text-right">
      <span
        className="font-mono tabular text-[14px]"
        style={{ color: tone ?? "var(--ink-1)", fontWeight: 400 }}
      >
        {value}
      </span>
      {hint && (
        <span
          className="block font-sans text-[10px] tracking-[0.08em] uppercase mt-1"
          style={{ color: "var(--ink-4)" }}
        >
          {hint}
        </span>
      )}
    </span>
  </li>
);

export const SupplyPage = () => {
  const { open } = useAppKit();
  const { address: account, chainId, isConnected } = useAccount();
  const { switchChainAsync } = useSwitchChain();
  const walletClient = useWalletClient();
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<SupplyMode>("supply");
  const [amountText, setAmountText] = useState("");
  const [submit, setSubmit] = useState<SubmitState>({ kind: "idle" });

  const wrongChain = isConnected && chainId !== ACTIVE_CHAIN.id;
  const isSupply = mode === "supply";

  const vaultStats = useVault();
  const { position: morphoPosition, refetch: refetchMorpho } = useVaultPosition(account);
  const { balance: usdcBalance } = useTokenBalance(USDC, account);

  const amountRaw = useMemo<bigint>(() => {
    try {
      return amountText ? parseUnits(amountText, USDC.decimals) : 0n;
    } catch {
      return 0n;
    }
  }, [amountText]);

  const walletRaw = usdcBalance?.raw ?? 0n;
  const suppliedRaw = morphoPosition?.suppliedRaw ?? 0n;

  const maxRaw = isSupply ? walletRaw : suppliedRaw;
  const overMax = amountRaw > maxRaw;

  const approvalNeeded =
    isSupply && amountRaw > 0n &&
    morphoPosition != null && morphoPosition.allowanceRaw < amountRaw;

  const formValid = amountRaw > 0n && !overMax;

  const onMax = () => {
    if (maxRaw === 0n) return;
    setAmountText(formatToken(maxRaw, USDC.decimals, 6).replace(/,/g, ""));
  };

  const onExecute = useCallback(async () => {
    if (!isConnected || !account) { await open(); return; }
    if (wrongChain) { await switchChainAsync({ chainId: ACTIVE_CHAIN.id }); return; }
    if (!walletClient.data) {
      setSubmit({ kind: "error", message: "Wallet client missing" });
      return;
    }
    setSubmit({ kind: "pending" });
    try {
      const fullExit = !isSupply && morphoPosition != null && amountRaw >= suppliedRaw;
      const hash = approvalNeeded
        ? await approveVaultUsdc(walletClient.data, { account })
        : isSupply
          ? await depositToVault(walletClient.data, { account, assetsRaw: amountRaw })
          : fullExit
            ? await redeemFromVault(walletClient.data, { account, sharesRaw: morphoPosition.shares })
            : await withdrawFromVault(walletClient.data, { account, assetsRaw: amountRaw });

      if (approvalNeeded) {
        await buildPublicClient().waitForTransactionReceipt({ hash });
        refetchMorpho();
        setSubmit({ kind: "idle" });
        return;
      }
      setSubmit({ kind: "mining", hash });
      const receipt = await buildPublicClient().waitForTransactionReceipt({ hash });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["balance"] }),
        queryClient.invalidateQueries({ queryKey: ["vault-position"] }),
        queryClient.invalidateQueries({ queryKey: ["vault-stats"] }),
      ]);
      if (receipt.status === "success") {
        setAmountText("");
        setSubmit({ kind: "success", hash });
      } else {
        setSubmit({ kind: "error", message: "Transaction reverted on chain." });
      }
    } catch (e) {
      console.error(e);
      const message = (e as { details?: string; message?: string })?.details ?? (e as { message?: string })?.message ?? "Submission failed";
      setSubmit({ kind: "error", message });
    }
  }, [
    isConnected, account, open, switchChainAsync, walletClient.data, wrongChain,
    isSupply, amountRaw, suppliedRaw, morphoPosition, approvalNeeded,
    queryClient, refetchMorpho,
  ]);

  const label = (() => {
    if (!isConnected) return "Connect wallet to continue";
    if (wrongChain) return "Switch to Base";
    if (submit.kind === "pending") return "Confirm in wallet…";
    if (submit.kind === "mining") return "Mining…";
    if (submit.kind === "success") return isSupply ? "Supplied" : "Withdrawn";
    if (amountRaw === 0n) return "Enter an amount";
    if (overMax) return isSupply ? "Exceeds balance" : "Exceeds position";
    if (approvalNeeded) return "Approve USDC";
    return isSupply ? "Supply USDC" : "Withdraw USDC";
  })();

  const disabled =
    isConnected && !wrongChain &&
    (!formValid || submit.kind === "pending" || submit.kind === "mining");

  const balanceLabel = isSupply
    ? `Balance · ${formatToken(walletRaw, USDC.decimals, 2)} ${USDC.symbol}`
    : `Supplied · ${formatToken(suppliedRaw, USDC.decimals, 2)} ${USDC.symbol}`;

  return (
    <article className="surface-strong rounded-[var(--radius-lg)] fade-up">
      <header className="px-5 md:px-9 pt-7 md:pt-9 pb-7">
        <div className="flex flex-col items-start gap-5 md:flex-row md:items-end md:justify-between md:gap-6">
          <div className="w-full md:w-auto">
            <div
              className="font-mono text-[10px] tracking-[0.22em] uppercase mb-3"
              style={{ color: "var(--ink-4)" }}
            >
              <span className="inline-flex items-center gap-1.5">
                <MorphoIcon size={11} />
                Vault · Morpho
                <span className="relative group flex items-center">
                  <span className="flex items-center cursor-default" style={{ color: "var(--ink-5)" }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden
                      stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"
                    >
                      <circle cx="12" cy="12" r="10"/>
                      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                      <path d="M12 17h.01"/>
                    </svg>
                  </span>
                  <div
                    className="pointer-events-none absolute left-0 bottom-full mb-2 z-50
                               opacity-0 group-hover:opacity-100
                               translate-y-1 group-hover:translate-y-0
                               transition-all duration-150"
                    role="tooltip"
                  >
                    <div
                      className="rounded-[var(--radius-md)] px-3 py-2.5 space-y-1.5 normal-case tracking-normal"
                      style={{
                        background: "#252525",
                        border: "1px solid var(--line-2)",
                        minWidth: "13rem",
                        maxWidth: "16rem",
                      }}
                    >
                      <div className="flex items-center gap-1.5" style={{ color: "var(--ink-3)" }}>
                        <AjnaIcon size={11} />
                        <span className="font-mono text-[10px] tracking-[0.04em]" style={{ color: "var(--ink-3)" }}>
                          Ajna
                        </span>
                      </div>
                      <p
                        className="font-sans text-[11px] leading-snug"
                        style={{ color: "var(--ink-2)", fontWeight: 300 }}
                      >
                        CADC liquidity must be supplied directly using the Ajna frontends.
                      </p>
                    </div>
                  </div>
                </span>
              </span>
            </div>
            <h2 className="text-[44px] md:text-[56px]" style={{ fontWeight: 350 }}>
              Supply USDC
            </h2>
          </div>
          <div className="flex w-full items-center justify-end gap-3 md:w-auto md:shrink-0">
            <ModeToggle value={mode} onChange={setMode} />
          </div>
        </div>
      </header>

      <div
        className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10 px-5 md:px-9 pb-7 pt-7"
        style={{ borderTop: "1px solid var(--line-2)" }}
      >
        <div className="space-y-9">
          <label className="block">
            <div className="flex items-baseline justify-between mb-3">
              <h3>{isSupply ? "Amount to supply" : "Amount to withdraw"}</h3>
              <button
                type="button"
                onClick={onMax}
                className="font-mono text-[10px] tracking-[0.16em] uppercase transition-opacity hover:opacity-70"
                style={{ color: "var(--ink-4)" }}
              >
                {balanceLabel}
              </button>
            </div>
            <div
              className="flex items-center gap-4 pl-5 pr-6 py-4 md:py-3 rounded-[var(--radius-md)] surface-strong"
              style={{
                borderWidth: 1,
                borderStyle: "solid",
                borderColor: overMax ? "var(--accent-rose)" : "var(--line-2)",
              }}
            >
              <input
                inputMode="decimal"
                placeholder="0.00"
                value={amountText}
                onChange={(e) => setAmountText(sanitise(e.target.value))}
                className="flex-1 min-w-0 bg-transparent border-0 outline-none focus:outline-none font-serif text-[23px] md:text-[30px] tabular"
                style={{ fontWeight: 300, letterSpacing: "-0.02em", color: "var(--ink-1)" }}
              />
              <div className="flex items-center gap-2 pl-4 border-l hairline shrink-0">
                <img src="/usdc.svg" alt="" aria-hidden className="w-7 h-7 rounded-full" />
                <span
                  className="font-sans text-[14px] tracking-[0.04em]"
                  style={{ color: "var(--ink-1)", fontWeight: 500 }}
                >
                  {USDC.symbol}
                </span>
              </div>
            </div>
            <p
              className="mt-3 font-sans text-[12px]"
              style={{ color: "var(--ink-3)", lineHeight: 1.5 }}
            >
              {isSupply
                ? "Deposited USDC is allocated into the Morpho CADC/USDC market that loopers borrow from."
                : "Withdraw your supplied USDC. Availability depends on current vault liquidity."}
            </p>
          </label>
        </div>

        <div className="space-y-9">
          <section>
            <div className="flex items-baseline justify-between mb-4">
              <h3>Vault</h3>
              <a
                href={SUPPLY_VAULT.href}
                target="_blank"
                rel="noopener noreferrer"
                className="font-mono text-[10px] tracking-[0.16em] uppercase transition-opacity hover:opacity-70"
                style={{ color: "var(--ink-4)" }}
              >
                {SUPPLY_VAULT.name} ↗
              </a>
            </div>
            <ul className="space-y-3">
              <StatRow
                label="Vault TVL"
                value={
                  vaultStats.data?.totalAssetsRaw == null
                    ? "—"
                    : `${formatToken(vaultStats.data.totalAssetsRaw, USDC.decimals, 0)} ${USDC.symbol}`
                }
              />
              <StatRow
                label="Net APY"
                value={vaultStats.data?.netApy == null ? "—" : formatPercent(vaultStats.data.netApy, 2)}
                tone="var(--accent-jade)"
              />
              <StatRow
                label="Your position"
                value={`${
                  morphoPosition ? formatToken(morphoPosition.suppliedRaw, USDC.decimals, 2) : "0.00"
                } ${USDC.symbol}`}
              />
              <StatRow
                label="Wallet balance"
                value={`${formatToken(walletRaw, USDC.decimals, 2)} ${USDC.symbol}`}
                last
              />
            </ul>
          </section>
        </div>
      </div>

      <footer
        className="px-5 md:px-9 py-7"
        style={{ borderTop: "1px solid var(--line-2)" }}
      >
        <ExecuteButton disabled={disabled} label={label} onClick={onExecute} />
        {submit.kind === "error" && (
          <p
            className="mt-4 font-mono text-[11px] tracking-[0.04em]"
            style={{ color: "var(--accent-rose)" }}
          >
            {submit.message}
          </p>
        )}
        {(submit.kind === "mining" || submit.kind === "success") && (
          <p
            className="mt-4 font-mono text-[11px] tabular tracking-[0.04em]"
            style={{ color: submit.kind === "success" ? "var(--accent-jade)" : "var(--ink-3)" }}
          >
            tx · {submit.hash.slice(0, 10)}…{submit.hash.slice(-8)}
          </p>
        )}
      </footer>
    </article>
  );
};
